import { mediaSha256 } from "./media-hash";
import { el } from "./lib";
import { mountPdfPreview, type PdfPreviewHandle } from "./pdf-preview";
import { bindPageAudioSync } from "./page-audio-sync";
import { parseRazSync, type RazSync } from "../shared/raz-sync";
import type { RazBook } from "../shared/raz";

async function verifiedMedia(url: string, sha256: string, type: string, signal: AbortSignal): Promise<string> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error("无法加载朗读材料");
  const bytes = await response.arrayBuffer();
  const hash = await mediaSha256(bytes);
  if (hash !== sha256) throw new Error("朗读材料版本不一致");
  signal.throwIfAborted();
  return URL.createObjectURL(new Blob([bytes], { type }));
}

/** 仅加载已确认的时间轴。缺失、损坏或版本不一致时仍提供普通 PDF 阅读。 */
export function mountRazReading(
  container: HTMLElement,
  book: RazBook,
  pdfUrl: string,
  signUrl: (key: string) => Promise<string>,
  previewFactory = mountPdfPreview,
): () => void {
  const reading = el("div", "raz-reading");
  const previewRoot = el("div", "raz-reading-preview");
  const status = el("p", "raz-reading-status", "正在加载绘本…");
  status.setAttribute("role", "status");
  reading.append(previewRoot, status);
  container.appendChild(reading);
  const controller = new AbortController();
  const objectUrls: string[] = [];
  let disposed = false;
  let preview: PdfPreviewHandle | undefined;
  let binding: ReturnType<typeof bindPageAudioSync> | undefined;
  let audio: HTMLAudioElement | undefined;
  function dispose() {
    if (disposed) return;
    disposed = true;
    controller.abort();
    binding?.dispose();
    audio?.pause();
    audio?.removeAttribute("src");
    preview?.();
    for (const url of objectUrls) URL.revokeObjectURL(url);
    observer.disconnect();
  }
  const observer = new MutationObserver(() => { if (!reading.isConnected) dispose(); });
  observer.observe(document.body, { childList: true, subtree: true });
  function plainReading(message = "") {
    if (disposed) return;
    status.textContent = message;
    status.hidden = !message;
    preview = previewFactory(previewRoot, pdfUrl, book.title);
  }
  void (async () => {
    let sync: RazSync;
    try {
      if (!book.audio) { plainReading(); return; }
      const response = await fetch(`/raz-sync/A/${encodeURIComponent(book.id)}.json`, { signal: controller.signal });
      if (!response.ok) { plainReading(); return; }
      sync = parseRazSync(await response.json());
      if (sync.bookId !== book.id || sync.pdf.key !== book.pdf || sync.audio.key !== book.audio) throw new Error("时间轴与绘本不一致");
    } catch {
      if (!disposed) plainReading();
      return;
    }
    try {
      status.textContent = "正在准备同步朗读…";
      const audioUrl = await signUrl(book.audio!);
      controller.signal.throwIfAborted();
      // 一次性校验并使用同一份媒体字节，防止替换文件后误套旧时间轴。
      async function load(url: string, hash: string, type: string) {
        const result = await verifiedMedia(url, hash, type, controller.signal);
        if (disposed) { URL.revokeObjectURL(result); throw new Error("disposed"); }
        objectUrls.push(result);
        return result;
      }
      const [pdfBlob, audioBlob] = await Promise.all([
        load(pdfUrl, sync.pdf.sha256, "application/pdf"),
        load(audioUrl, sync.audio.sha256, "audio/mpeg"),
      ]);
      if (disposed) return;
      status.hidden = true;
      const controls = el("div", "raz-reading-audio");
      const label = el("label", "raz-follow-label");
      const follow = document.createElement("input");
      follow.type = "checkbox"; follow.checked = true;
      label.append(follow, document.createTextNode("跟随朗读翻页"));
      const notice = el("span", "raz-sync-notice", "点击播放，绘本会自动翻页");
      notice.setAttribute("role", "status");
      audio = document.createElement("audio");
      audio.controls = true; audio.preload = "metadata"; audio.src = audioBlob;
      audio.setAttribute("aria-label", `${book.title} 配套朗读`);
      controls.append(label, notice, audio);
      reading.appendChild(controls);
      binding = bindPageAudioSync(audio, sync.segments, page => preview?.goToPage(page), (following, reason) => {
        follow.checked = following;
        notice.textContent = reason ?? (following ? "已开启自动翻页" : "自由翻页，音频继续播放");
      });
      follow.addEventListener("change", () => binding?.setFollowing(follow.checked));
      let invalid = false;
      function disableSync(message: string) {
        if (invalid) return;
        invalid = true;
        binding?.dispose();
        audio?.pause();
        follow.checked = false; follow.disabled = true;
        notice.textContent = message;
      }
      audio.addEventListener("loadedmetadata", () => {
        if (!Number.isFinite(audio!.duration) || Math.abs(audio!.duration - sync.audio.duration) > 0.25) disableSync("音频时长不一致，已关闭同步");
      });
      audio.addEventListener("error", () => disableSync("音频暂时无法播放，仍可阅读绘本"));
      preview = previewFactory(previewRoot, pdfBlob, book.title, undefined, {
        onPageChange(page, total, source) {
          if (total !== sync.pdf.pageCount) { disableSync("绘本页数不一致，已关闭同步"); return; }
          binding?.onPageChange(page, source);
        },
      });
    } catch {
      if (!disposed) plainReading("同步朗读暂不可用，可继续阅读绘本");
    }
  })();
  return dispose;
}
